import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import type { Permission, UserRole } from '../lib/api';
import {
  IcHome,
  IcCreditCard,
  IcPill,
  IcTruck,
  IcFile,
  IcReceipt,
  IcBuilding,
  IcTag,
  IcSettings,
  IcWebhook,
  IcChevron,
  IcLogOut,
  IcUsers,
  type IconComponent,
} from './icons';

type NavItem = {
  label: string;
  to: string;
  icon: IconComponent;
  count?: number;
  highlight?: boolean;
  /** Permission required to see this item; omitted = visible to any signed-in user. */
  perm?: Permission;
};
type Section = { title: string; items: NavItem[] };

/**
 * Grouped by what the money is doing, then ordered by how often the page is
 * opened: the daily collection work first, the periodic runs and the reference
 * tables below it. Dashboard stands alone rather than costing a header row.
 */
const SECTIONS: Section[] = [
  {
    title: '',
    items: [{ label: 'Dashboard', to: '/', icon: IcHome, perm: 'dashboard:read' }],
  },
  {
    title: 'RECEIVABLES',
    items: [
      { label: 'Invoices', to: '/invoices', icon: IcFile, perm: 'invoices:read' },
      { label: 'Accounts Receivable', to: '/ar', icon: IcFile, perm: 'invoices:read' },
      // Cards on file are a tab here — they are what these charges run against.
      { label: 'Payments', to: '/payments', icon: IcCreditCard, perm: 'payments:read' },
    ],
  },
  {
    title: 'PAYABLES',
    items: [
      { label: 'Pharmacy Payables', to: '/payables', icon: IcReceipt, perm: 'dashboard:read' },
      // What FedEx bills us — money out, however long it sat under BILLING.
      { label: 'FedEx Bills', to: '/fedex', icon: IcTruck, perm: 'dashboard:read' },
    ],
  },
  {
    title: 'BILLING RUNS',
    items: [
      { label: 'Platform Fees', to: '/platform', icon: IcCreditCard, perm: 'dashboard:read' },
      // Shipping and volume are tabs: both describe this same combined invoice.
      { label: 'Pharmacy Fees', to: '/pharmacy', icon: IcPill, perm: 'dashboard:read' },
    ],
  },
  {
    title: 'RATES & ACCOUNTS',
    items: [
      { label: 'Clients', to: '/clients', icon: IcBuilding, perm: 'clients:read' },
      // Coverage — which dispensed products still have no price — is a tab.
      { label: 'Pricing', to: '/pricing', icon: IcTag, perm: 'pricing:read' },
      { label: 'Pharmacy Costs', to: '/pharmacy-costs', icon: IcTag, perm: 'dashboard:read' },
      { label: 'Users', to: '/users', icon: IcUsers, perm: 'users:read' },
    ],
  },
  {
    title: 'SYSTEM',
    items: [
      { label: 'Settings', to: '/settings', icon: IcSettings },
      { label: 'Webhooks', to: '/webhooks', icon: IcWebhook, perm: 'dashboard:read' },
    ],
  },
];

const ROLE_LABEL: Record<UserRole, string> = {
  super_admin: 'Super admin',
  accounting: 'Accounting',
  clinic_view: 'Clinic view',
};

export const Sidebar = ({ open = false, onNavigate }: { open?: boolean; onNavigate?: () => void }) => {
  const { user, can, logout } = useAuth();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Hide nav items the user can't access, and drop sections left empty.
  const sections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((i) => !i.perm || can(i.perm)),
  })).filter((s) => s.items.length > 0);

  return (
    <aside
      className={`app-sidebar${open ? ' app-sidebar--open' : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--p-sidebar-bg)',
        color: 'var(--p-sidebar-fg)',
        borderRight: '1px solid var(--p-sidebar-border)',
        fontFamily: 'var(--font-product)',
      }}
    >
      <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src="/assets/5axis-icon.svg"
            alt="5 Axis"
            style={{ width: 26, height: 26, filter: 'brightness(0) invert(1)' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
              5 Axis Billing
            </span>
            <span style={{ fontSize: 11, color: 'rgba(147,197,253,.5)' }}>Billing Console</span>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav" style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {sections.map((section) => {
          const isC = collapsed[section.title];
          return (
            <div key={section.title} style={{ marginBottom: 4 }}>
              {section.title !== '' && <button
                onClick={() => setCollapsed((p) => ({ ...p, [section.title]: !p[section.title] }))}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 20px',
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,.3)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {section.title}
                <IcChevron size={12} style={{ transform: isC ? 'rotate(-90deg)' : 'none', transition: 'transform .15s ease' }} />
              </button>}
              {!isC && (
                <div style={{ padding: '0 12px 4px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {section.items.map((item) => (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      end={item.to === '/'}
                      onClick={onNavigate}
                      style={({ isActive }) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 12px',
                        borderRadius: 6,
                        fontSize: 13,
                        color: isActive ? '#fff' : 'rgba(255,255,255,.5)',
                        background: isActive ? 'rgba(255,255,255,.1)' : 'transparent',
                        fontWeight: isActive ? 500 : 400,
                        cursor: 'pointer',
                        transition: 'all .15s ease',
                      })}
                    >
                      <item.icon size={16} />
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.count !== undefined && (
                        <span
                          className="tabular"
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 999,
                            fontWeight: 600,
                            background: item.highlight ? 'rgba(251,191,36,.9)' : 'rgba(255,255,255,.1)',
                            color: item.highlight ? '#451a03' : 'rgba(255,255,255,.5)',
                          }}
                        >
                          {item.count.toLocaleString()}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)', marginBottom: 8 }}>
          {user?.name}
          {user && (
            <span style={{ color: 'rgba(255,255,255,.3)' }}> · {ROLE_LABEL[user.role]}</span>
          )}
        </div>
        <button
          onClick={() => void logout()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'rgba(255,255,255,.35)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <IcLogOut size={14} /> Sign out
        </button>
      </div>
    </aside>
  );
};
